<template>
  <div>
    <v-card class="mt-n3">
      <v-card-title class="mr-4 files-header">
        <v-tooltip bottom style="z-index: 99" class="py-0 pl-0 pr-2 mt-2">
          <template v-slot:activator="{ on }">
            <v-btn x-small fab @click="refreshFirmFiles(fid, page, itemsPerPage)" v-on="on"
                   class="px-3 files-refresh-button" elevation="5"><v-icon>mdi-update</v-icon>
            </v-btn>
          </template>
          <span>Refresh Files</span>
        </v-tooltip>
        <div class="firm-files-toolbar mt-n1">
          <information-dialog-icon
            class="toolbar-info"
            confirmClass="mr-3 mb-1 title"
            confirmColor="secondary darken-1"
            :dialog-width="infoDialogWidth"
            icon-color="grey darken-1"
            :icon-style="infoIconStyle"
            title="Files Table Information"
            :tclass="infoDialogTitleClass"
          >
            <div :class="['files-info-dialog-copy', { 'files-info-dialog-copy--compact': isCompactToolbar }]">
              <template v-if="isCompactToolbar">
                <p>The search box filters only the files currently shown in this table.</p>
                <p>The count reflects the files shown here. Some file categories are not displayed in this table, so total file counts may be higher.</p>
                <p>Normal view loads up to {{ loadedFileLimitText }} recent files. <strong>Search all files</strong> shows the first matching batch for this table view, up to {{ loadedFileLimitText }} files.</p>
                <p><strong>Options</strong> changes how many files are shown and how <strong>Search all files</strong> searches. Use the button next to the search box to return to the normal list.</p>
              </template>
              <template v-else>
                <p>The search box filters only the files currently shown in this table.</p>
                <p>The count reflects the files shown here and, when available, the firm's total file count.</p>
                <p>In normal table view, this table loads up to {{ loadedFileLimitText }} recent files from the database and shows the files that belong in this view.</p>
                <p>Some file categories are not displayed in this table, so total file counts may be higher than the number shown here.</p>
                <p><strong>Search all files</strong> searches the firm's files and shows the first matching batch that belongs in this table view, up to {{ loadedFileLimitText }} files, ordered by the database search options.</p>
                <p><strong>Options</strong> lets you choose how many files to show in this table and how <strong>Search all files</strong> searches the database. Use the button next to the search box to return to the normal list.</p>
              </template>
              <p v-if="canEditFileDescriptions">File descriptions in this table can be edited. Press Return to save a description change.</p>
            </div>
          </information-dialog-icon>
          <div class="search-stack">
            <v-text-field
              ref="searchField"
              :value="searchText"
              append-icon="mdi-magnify"
              clearable
              dense
              hide-details
              label="Search"
              single-line
              class="search-input"
              @input="onSearchInput"
              @click:clear="clearSearch()"
            ></v-text-field>
            <div
              class="caption result-summary supporting-text"
              :title="countSummaryTitle"
            >
              {{ countSummaryText }}
            </div>
          </div>
          <v-tooltip bottom style="z-index: 99">
            <template v-slot:activator="{ on }">
              <span v-on="on">
                <v-btn
                  small
                  :class="{ 'toolbar-action--back': showServerSearchSummary }"
                  :color="showServerSearchSummary ? 'secondary darken-1' : undefined"
                  :outlined="showServerSearchSummary"
                  :text="!showServerSearchSummary"
                  class="toolbar-action toolbar-action--primary"
                  :disabled="primarySearchActionDisabled"
                  @click="onPrimarySearchAction()"
                >
                  <v-icon left small>{{ showServerSearchSummary ? 'mdi-arrow-left' : 'mdi-cloud-search-outline' }}</v-icon>
                  {{ primarySearchActionText }}
                </v-btn>
              </span>
            </template>
            <span>{{ primarySearchActionTooltip }}</span>
          </v-tooltip>
          <component
            :is="optionsOverlayComponent"
            v-model="advancedSearchOpen"
            v-bind="optionsOverlayProps"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn small text class="toolbar-action toolbar-action--options" v-bind="attrs" v-on="on">
                <v-icon left small>mdi-tune</v-icon>
                Options
              </v-btn>
            </template>

            <v-card
              :class="[
                'table-options-card',
                isCompactToolbar ? 'table-options-card--dialog pa-4' : 'table-options-card--menu pa-3'
              ]"
            >
              <div class="subtitle-2 font-weight-medium table-options-title">Table Options</div>
              <div class="supporting-text table-options-copy mt-2">
                Choose how many files to show here. The settings below apply only to
                <strong>Search all files</strong>, which shows the first matching batch in this table.
              </div>
              <v-row dense class="mt-1">
                <v-col cols="12">
                  <div class="table-options-field-label">Files to show in table</div>
                  <v-select
                    :value="itemsPerPage"
                    :items="loadedFileLimitOptions"
                    item-text="text"
                    item-value="value"
                    dense
                    hide-details
                    @change="onLoadedFileLimitChange"
                  ></v-select>
                  <div class="caption supporting-text table-options-note mt-1">
                    Higher limits can slow local filtering.
                  </div>
                </v-col>
                <v-col cols="12" class="pb-0">
                  <div class="table-options-field-label mt-2">Database search</div>
                </v-col>
                <v-col cols="12">
                  <div class="table-options-field-label">Search in these fields</div>
                  <v-select
                    v-model="searchScope"
                    :items="searchScopeOptions"
                    item-text="text"
                    item-value="value"
                    dense
                    hide-details
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="table-options-field-label">Sort results by</div>
                  <v-select
                    v-model="searchOrder"
                    :items="searchOrderOptions"
                    item-text="text"
                    item-value="value"
                    dense
                    hide-details
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="table-options-field-label">Sort order</div>
                  <v-select
                    v-model="searchDirection"
                    :items="searchDirectionOptions"
                    item-text="text"
                    item-value="value"
                    dense
                    hide-details
                  ></v-select>
                </v-col>
              </v-row>
              <div v-if="serverSearchUnavailable" class="caption error--text mt-2">
                {{ serverSearchError || 'Server search is not available on this backend right now.' }}
              </div>
              <div class="supporting-text table-options-copy mt-2">
                Search above filters only the files currently shown in this table. Normal view loads up to {{ loadedFileLimitText }} recent files and shows the files that belong in this view.
                <strong>Search all files</strong> shows the first matching batch that belongs in this table view, up to {{ loadedFileLimitText }} rows.
              </div>
              <div class="mt-2 table-options-actions">
                <v-btn text small @click="advancedSearchOpen = false">Close</v-btn>
                <v-btn
                  color="secondary"
                  small
                  :disabled="!canSearch || loading || serverSearchUnavailable"
                  @click="runSearch()"
                >
                  {{ isCompactToolbar ? 'Search all' : 'Search all files' }}
                </v-btn>
              </div>
            </v-card>
          </component>
          <FirmFilesUpload class="upload-button-wrap" v-bind="{ fid }" @filesUploaded="filesUploaded"/>
        </div>
      </v-card-title>
    </v-card>
      <v-data-table
          class="elevation-2"
          :class="[!notPopup ? 'popupLargeTableNoFooter' : 'largeSizeTableNoFooter']"
          dense
          disable-pagination
          fixed-header
          :headers="colHeaders"
          item-key="loid"
          :items="displayedFiles"
          hide-default-footer
          :items-per-page="itemsPerPage"
          :loading="loading"
          loading-text="Data loading... Please wait..."
          :no-data-text="noDataText"
          multi-sort
          :mobile-breakpoint="0"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :style="$_cssVars"
      >
        <template v-slot:item.filename="{ item }">
          <v-lazy>
            <div>
              <v-tooltip left v-if="item.filename && item.filename.length > cutoff">
                <template v-slot:activator="{ on }">
                  <div v-on="on">{{ item.filename ? item.filename.slice(0, cutoff - 3) + '...' : '' }}</div>
                </template>
                {{ item.filename ? item.filename : '' }}
              </v-tooltip>
              <span v-else>{{ item.filename || '' }}</span>
            </div>
          </v-lazy>
        </template>
        <template v-slot:item.content_type="{ item }">
          <v-lazy>
            <div>
              <v-tooltip bottom nudge-top="33" v-if="item.content_type && item.content_type.length > cutoff">
                <template v-slot:activator="{ on }">
                  <div v-on="on">{{ item.content_type ? item.content_type.slice(0, cutoff - 3) + '...' : '' }}</div>
                </template>
                {{ item.content_type || '' }}
              </v-tooltip>
              <span v-else>{{ item.content_type ? item.content_type : '' }}</span>
            </div>
          </v-lazy>
        </template>
        <template v-slot:item.file_description="{ item }">
	        <v-lazy>
            <firm-files-description-cell
              :description="item.file_description"
              :download-href="`${url}/download/${item.loidfid}`"
              :editable="isDescriptionEditable(item)"
              :rules="rules"
              @save-description="saveDescription(item, $event)"
            />
          </v-lazy>
        </template>
        <template v-slot:item.dated="{ item }">
          <v-lazy>
            <span>
              {{ item._displayDateText || formatDisplayDate(item.dated) }}
            </span>
          </v-lazy>
        </template>
        <template v-slot:item.file_type_description="{ item }">
	        <v-lazy>
            <v-edit-dialog :return-value="item.file_type_description">
              {{ item.file_type_description }}
              <template v-slot:input v-can="'firm-edit'">
                <v-autocomplete
                  class="px-2 mb-6"
                  :items="types"
                  persistent-hint hint="Press Return to Save"
                  @keyup.enter="save(item, 'type')"
                  v-model="item.file_type_description"
                ></v-autocomplete>
              </template>
            </v-edit-dialog>
	        </v-lazy>
        </template>
    </v-data-table>
  </div>
</template>

<script>
import FirmFilesUpload from "./FirmFilesUpload";
import FirmFilesDescriptionCell from "./FirmFilesDescriptionCell";
import InformationDialogIcon from "../../InformationDialogIcon";
import { FileobjectsAPIService } from '@/servicehandlers/FileobjectsAPIService';
import { FirmsAPIService } from '@/servicehandlers/FirmsAPIService';
import _ from 'lodash';

const apiService = new FirmsAPIService();
const file_objects = new FileobjectsAPIService();
const SEARCH_RESULT_FIELDS = [
  'loid',
  'fid',
  'filename',
  'content_type',
  'file_description',
  'file_type(file_type) as file_type_description',
  'file_type(file_type) as f_type',
  'diencrypt(loid||chr(44)||fid) as loidfid',
  'dated',
  'size',
].join(',');
const SEARCH_SCOPE_OPTIONS = [
  { text: 'Filename + Description', value: 'filename,file_description' },
  { text: 'Filename only', value: 'filename' },
  { text: 'Description only', value: 'file_description' },
];
const SEARCH_ORDER_OPTIONS = [
  { text: 'Date', value: 'dated' },
  { text: 'Filename', value: 'filename' },
  { text: 'Description', value: 'file_description' },
  { text: 'Content type', value: 'content_type' },
];
const SEARCH_DIRECTION_OPTIONS = [
  { text: 'Newest / Z-A', value: 'DESC' },
  { text: 'Oldest / A-Z', value: 'ASC' },
];
const LOADED_FILE_LIMIT_OPTIONS = [
  { text: '1,000', value: 1000 },
  { text: '2,000', value: 2000 },
  { text: '5,000', value: 5000 },
];
const LOCAL_FILTER_DEBOUNCE_MS = 200;

export default {
  name: "FirmFilesTable",
  props: {
    firm: Object,
    notPopup: Boolean,
    default() {
      return {};
    }
  },
  components: { FirmFilesUpload, FirmFilesDescriptionCell, InformationDialogIcon },
  data() {
    return {
      colHeaders: [
        {value:'filename',             text:'Filename',    width: '230px', align: 'start'},
        {value:'content_type',         text:'Content Type',width: '150px', align: 'start'},
        {value:'file_description',     text:'Description', width: '460px', align: 'start'},
        {value:'file_type_description',text:'Type',        width:  '80px', align: 'start'},
        {value:'dated',                text:'Dated',       width: '180px', align: 'start'},
      ],
      fid: this.firm.fid,
      files: [],
      page: 1,
      itemsPerPage: 2000,
      allFilesTotalCount: 0,
      sortBy: ['dated'],
      sortDesc: [true],
      totalItems: 0,
      _fetchFirmFilesFromApi: null, // placeholder initialized during create()
      activeSearchDirection: 'DESC',
      activeSearchOrder: 'dated',
      activeSearchScope: 'filename,file_description',
      activeSearchText: '',
      advancedSearchOpen: false,
      groups: [],
      loadedFileLimitOptions: LOADED_FILE_LIMIT_OPTIONS,
      loading: false,
      rules: [
        (v) => !!v || 'Description is required to upload a file',
      ],
      searchActive: false,
      searchDirection: 'DESC',
      searchDirectionOptions: SEARCH_DIRECTION_OPTIONS,
      searchOrder: 'dated',
      searchOrderOptions: SEARCH_ORDER_OPTIONS,
      searchScope: 'filename,file_description',
      searchScopeOptions: SEARCH_SCOPE_OPTIONS,
      searchText: '',
      serverSearchError: '',
      serverSearchUnavailable: false,
      url: process.env.VUE_APP_UW_URL,
    }
  },

  created() {
    this.groups = this.$store.getters.groups;
    this._liveSearchText = '';
    this._applySearchText = _.debounce((value) => {
      this.searchText = (value || '').toString();
    }, LOCAL_FILTER_DEBOUNCE_MS);
    this._liveSearchText = this.normalizedSearchText;
    this._fetchFirmFilesFromApi = this.createMemoizedFetch();
    this.getFirmFiles(this.fid);
  },

  beforeDestroy() {
    this.advancedSearchOpen = false;
    if (this._applySearchText && this._applySearchText.cancel) {
      this._applySearchText.cancel();
    }
  },

  computed: {
    types() {
      let canEdit = this.groups.includes('admin') || this.$_can('firmfile-override');
      let types = ['Admin', 'Original', 'Other'];
      if (canEdit) types = types.concat('Form');// if user canEdit show 'Form' as an option
      return types;
    },
    cutoff() {
      return !this.notPopup ? 15 : 30;
    },
    canEditFileDescriptions() {
      return this.groups.includes('admin') || this.$_can('firm-edit');
    },
    isCompactToolbar() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    infoDialogWidth() {
      return this.isCompactToolbar ? 460 : 560;
    },
    infoDialogTitleClass() {
      return this.isCompactToolbar
        ? 'subtitle-1 grey lighten-2 mb-4'
        : 'headline grey lighten-2 mb-8';
    },
    optionsOverlayComponent() {
      return this.isCompactToolbar ? 'v-dialog' : 'v-menu';
    },
    optionsOverlayProps() {
      if (this.isCompactToolbar) {
        return {
          maxWidth: 420,
        };
      }

      return {
        closeOnContentClick: false,
        eager: true,
        offsetY: true,
        transition: 'scale-transition',
      };
    },
    infoIconStyle() {
      return this.isCompactToolbar
        ? 'transform: translate(6px, 1px);'
        : 'transform: translate(20px, 1px);';
    },
    normalizedSearchText() {
      return (this.searchText || '').toString();
    },
    normalizedActiveSearchText() {
      return (this.activeSearchText || '').toString();
    },
    canSearch() {
      return !!this.normalizedSearchText.trim();
    },
    isPendingLocalFilter() {
      return this.searchActive && this.normalizedSearchText.trim() !== this.normalizedActiveSearchText.trim();
    },
    showServerSearchSummary() {
      return this.searchActive && !this.isPendingLocalFilter;
    },
    visibleFileCount() {
      return Array.isArray(this.displayedFiles) ? this.displayedFiles.length : 0;
    },
    hasLocalFilter() {
      return !!this.normalizedSearchText.trim() && !this.showServerSearchSummary;
    },
    noDataText() {
      if (this.showServerSearchSummary) return 'No files matched your server search.';
      if (this.searchActive && this.isPendingLocalFilter) return 'No current results matched your filter.';
      if (this.hasLocalFilter) return 'No files shown here matched your filter.';
      return 'No files found.';
    },
    countSummaryText() {
      const visibleCount = this.visibleFileCount;
      const totalCount = Number(this.totalItems) || 0;
      const allFilesTotalCount = Number(this.allFilesTotalCount) || totalCount;
      const loadedCount = Array.isArray(this.files) ? this.files.length : 0;

      if (this.loading && !this.searchActive && loadedCount === 0 && totalCount === 0) {
        return 'Loading files now';
      }

      if (this.showServerSearchSummary) {
        if (allFilesTotalCount > 0) {
          return `${visibleCount.toLocaleString()} matches shown of ${allFilesTotalCount.toLocaleString()} files`;
        }
        if (this.itemsPerPage && visibleCount >= this.itemsPerPage) {
          return `Showing first ${visibleCount.toLocaleString()} matches`;
        }
        return `${visibleCount.toLocaleString()} match${visibleCount === 1 ? '' : 'es'}`;
      }

      if (this.searchActive && this.isPendingLocalFilter) {
        if (allFilesTotalCount > 0) {
          return `${visibleCount.toLocaleString()} matches in ${loadedCount.toLocaleString()} shown here, ${allFilesTotalCount.toLocaleString()} total files`;
        }
        return `${visibleCount.toLocaleString()} matches in ${loadedCount.toLocaleString()} current results`;
      }

      if (this.hasLocalFilter) {
        return `${visibleCount.toLocaleString()} matches in ${loadedCount.toLocaleString()} shown here`;
      }

      if (totalCount > loadedCount && loadedCount > 0) {
        return `${loadedCount.toLocaleString()} shown here, ${totalCount.toLocaleString()} total files`;
      }

      if (totalCount > 0) {
        return `${visibleCount.toLocaleString()} of ${totalCount.toLocaleString()} files`;
      }

      if (loadedCount > 0) {
        return `${loadedCount.toLocaleString()} file${loadedCount === 1 ? '' : 's'} currently shown`;
      }

      return '0 files are currently shown';
    },
    countSummaryTitle() {
      const totalCount = Number(this.totalItems) || 0;
      const allFilesTotalCount = Number(this.allFilesTotalCount) || totalCount;
      const loadedCount = Array.isArray(this.files) ? this.files.length : 0;

      if (this.loading && !this.searchActive && loadedCount === 0 && totalCount === 0) {
        return 'Loading files now.';
      }

      if (this.showServerSearchSummary) {
        const limitNote = this.itemsPerPage && this.visibleFileCount >= this.itemsPerPage
          ? ` Results in this table are limited to ${this.itemsPerPage.toLocaleString()} rows.`
          : '';
        const totalFilesNote = allFilesTotalCount > 0
          ? ` Searching across ${allFilesTotalCount.toLocaleString()} total files.`
          : '';
        return `Server search results for "${this.normalizedActiveSearchText}". Showing the first matching batch that belongs in this table view.${totalFilesNote}${limitNote}`;
      }

      if (this.searchActive && this.isPendingLocalFilter) {
        const totalFilesNote = allFilesTotalCount > 0
          ? ` Current server results are from ${allFilesTotalCount.toLocaleString()} total files.`
          : '';
        return `Filtering the current server batch locally.${totalFilesNote}`;
      }

      if (totalCount > loadedCount && loadedCount > 0) {
        return `This table is showing ${loadedCount.toLocaleString()} files here out of ${totalCount.toLocaleString()} total files. Some file categories are not displayed in this table.`;
      }

      return this.countSummaryText;
    },
    loadedFileLimitText() {
      const limit = Number(this.itemsPerPage) || 0;
      return limit.toLocaleString();
    },
    displayedFiles() {
      if (this.showServerSearchSummary) return this.files;
      return this.filterFiles(this.files, this.searchText);
    },
    primarySearchActionText() {
      if (this.isCompactToolbar) {
        return this.showServerSearchSummary ? 'See table' : 'Search all';
      }
      return this.showServerSearchSummary ? 'Show table files' : 'Search all files';
    },
    primarySearchActionTooltip() {
      if (this.showServerSearchSummary) {
        return 'Return to the normal table list';
      }
      if (this.serverSearchUnavailable) {
        return 'Server search is not available on this backend right now';
      }
      if (!this.canSearch) {
        return 'Enter a search term first';
      }
      return 'Search across all firm files, not just the files currently shown in this table';
    },
    primarySearchActionDisabled() {
      if (this.showServerSearchSummary) return this.loading;
      return !this.canSearch || this.loading || this.serverSearchUnavailable;
    },
  },

  watch: {
    isCompactToolbar() {
      this.advancedSearchOpen = false;
    },
  },

  methods: {

    createMemoizedFetch() {
      return _.memoize(
        (fid, page, itemsPerPage, router) => apiService.getFirmFiles(fid, page, itemsPerPage, router),
        (fid, page, itemsPerPage) => `${fid}-${page}-${itemsPerPage}` // cache key
      );
    },

    refreshFirmFiles(fid, page = 1, itemsPerPage = this.itemsPerPage) {
      this.serverSearchUnavailable = false;
      this.serverSearchError = '';
      if (this.searchActive && this.activeSearchText) {
        this.runSearch({ useActiveCriteria: true });
        return;
      }
      this._fetchFirmFilesFromApi = this.createMemoizedFetch(); // clear cache by creating a new instance
      this.getFirmFiles(fid, page, itemsPerPage);
    },

    onLoadedFileLimitChange(newLimit) {
      const normalizedLimit = Number(newLimit);
      if (!normalizedLimit || normalizedLimit === this.itemsPerPage) return;

      this.itemsPerPage = normalizedLimit;
      this.page = 1;
      this._fetchFirmFilesFromApi = this.createMemoizedFetch();

      if (this.searchActive && this.activeSearchText) {
        this.runSearch({ useActiveCriteria: true });
        return;
      }

      this.getFirmFiles(this.fid, 1, normalizedLimit);
    },

    getLiveSearchText() {
      const fieldValue = this.$refs.searchField && this.$refs.searchField.lazyValue !== undefined
        ? this.$refs.searchField.lazyValue
        : this._liveSearchText;
      return (fieldValue || '').toString();
    },

    onSearchInput(value) {
      const normalized = (value || '').toString();
      this._liveSearchText = normalized;
      if (!this._applySearchText) {
        this.searchText = normalized;
        return;
      }
      this._applySearchText(normalized);
    },

    setSearchTextImmediately(value) {
      const normalized = (value || '').toString();
      if (this._applySearchText && this._applySearchText.cancel) {
        this._applySearchText.cancel();
      }
      this._liveSearchText = normalized;
      this.searchText = normalized;
    },

    getFirmFiles(id, page = 1, itemsPerPage = this.itemsPerPage) {
      this.loading = true;
      const firmId = id.toString();
      this._fetchFirmFilesFromApi(firmId, page, itemsPerPage, this.$router).then((data) => {
        const files = this.normalizeFiles(data.result || []);
        const totalCount = data.count ?? files.length;
        this.files = files;
        this.totalItems = totalCount; // total number of records
        this.allFilesTotalCount = totalCount;
        this.page = page;
      }).catch((error) => {
        console.warn('Failed to load firm files:', error);
        this.files = [];
        this.allFilesTotalCount = 0;
        this.totalItems = 0;
      }).finally(() => {
        this.loading = false;
      });
    },

    buildSearchParams(useActiveCriteria = false) {
      const rawSearch = useActiveCriteria ? this.normalizedActiveSearchText : this.getLiveSearchText();
      return {
        page: 1,
        search: rawSearch.trim(),
        fields: SEARCH_RESULT_FIELDS,
        fieldsToSearch: useActiveCriteria ? this.activeSearchScope : this.searchScope,
        orders: useActiveCriteria ? this.activeSearchOrder : this.searchOrder,
        directions: useActiveCriteria ? this.activeSearchDirection : this.searchDirection,
        limit: this.itemsPerPage,
      };
    },

    onPrimarySearchAction() {
      if (this.showServerSearchSummary) {
        this.returnToLoadedFiles();
        return;
      }
      this.runSearch();
    },

    runSearch({ useActiveCriteria = false } = {}) {
      if (this.serverSearchUnavailable) return;
      const params = this.buildSearchParams(useActiveCriteria);
      if (!params.search) {
        this.clearSearch();
        return;
      }

      this.setSearchTextImmediately(params.search);
      this.loading = true;
      this.serverSearchError = '';
      apiService.fileSearch(this.fid, params, this.$router).then((data) => {
        const rawRows = Array.isArray(data) ? data : data?.result;
        if (!Array.isArray(rawRows)) {
          throw new Error('Unexpected file search response');
        }
        const files = this.normalizeFiles(rawRows);
        this.files = files;
        this.totalItems = files.length;
        this.page = 1;
        this.searchActive = true;
        this.activeSearchText = params.search;
        this.activeSearchScope = params.fieldsToSearch;
        this.activeSearchOrder = params.orders;
        this.activeSearchDirection = params.directions;
        this.setSearchTextImmediately(params.search);
        this.advancedSearchOpen = false;
      }).catch((error) => {
        if (error?.response?.status === 404) {
          this.serverSearchUnavailable = true;
          this.serverSearchError = 'Server search is not available on this backend yet.';
        }
        console.warn('Firm file search failed:', error);
      }).finally(() => {
        this.loading = false;
      });
    },

    returnToLoadedFiles() {
      this.searchActive = false;
      this.activeSearchText = '';
      this.page = 1;
      this.setSearchTextImmediately(this.getLiveSearchText());
      this._fetchFirmFilesFromApi = this.createMemoizedFetch();
      this.getFirmFiles(this.fid, 1, this.itemsPerPage);
    },

    clearSearch() {
      const wasSearching = this.searchActive;
      this.setSearchTextImmediately('');
      this.searchActive = false;
      this.activeSearchText = '';
      this.page = 1;
      if (!wasSearching) return;
      this._fetchFirmFilesFromApi = this.createMemoizedFetch();
      this.getFirmFiles(this.fid, 1, this.itemsPerPage);
    },

    filterFiles(rows, search) {
      const query = (search || '')
        .toString()
        .trim()
        .toLowerCase();
      if (!query) return rows;

      return (Array.isArray(rows) ? rows : []).filter((row) => {
        const haystack = this.buildRowSearchText(row);
        return haystack.includes(query);
      });
    },

    buildRowSearchText(row) {
      return [
        row?._staticSearchText || '',
        row?.file_description || '',
        row?.file_type_description || row?.f_type || '',
      ].join(' ').toLowerCase();
    },

    isDescriptionEditable(item) {
      return this.groups.includes('admin') || (this.$_can('firm-edit') && item.file_type_description !== 'Form');
    },

    saveDescription(item, value) {
      item.file_description = (value || '').toString();
      this.save(item, 'description');
    },

    formatDisplayDate(value) {
      const m = this.moment ? this.moment(value) : null;
      if (!m || !m.isValid()) return '';
      return m.format('DD MMM YYYY hh:mm A');
    },

    normalizeFiles(rows) {
      let files = Array.isArray(rows) ? rows.map((file) => {
        const fileTypeDescription = file.file_type_description || file.f_type || this.getTypeDescription(file.file_type);
        const displayDateText = this.formatDisplayDate(file.dated);
        const staticSearchText = [
          file.filename,
          file.content_type,
          file.size,
          displayDateText,
        ].filter(value => value !== null && value !== undefined && value !== '').join(' ');
        return {
          ...file,
          _displayDateText: displayDateText,
          file_type_description: fileTypeDescription,
          f_type: file.f_type || fileTypeDescription,
          _staticSearchText: staticSearchText,
        };
      }) : [];

      files = files.filter(file => file.file_type_description !== 'Form');
      if (!this.groups.includes('admin')) {
        files = files.filter(file => file.file_type_description !== 'Admin');
      }

      return files;
    },

    getTypeDescription(fileType) {
      switch (Number(fileType)) {
        case 1:
          return 'Admin';
        case 3:
          return 'Form';
        case 6:
          return 'Other';
        case 17:
          return 'Original';
        default:
          return '';
      }
    },

    getType(description) {// 1-Admin 3-Form 6-Other 17-Original
      switch(description) {
        case 'Admin':
          return 1;
        case 'Form':
          return 3;
        case 'Other':
          return 6;
        case 'Original':
          return 17;
        default:
          return 6;
      }
    },

    save(file, field) {
      const my_file_object = field === 'description' ? { file_description: file.file_description }
        : { file_type: this.getType(file.file_type_description) };
      file_objects.updateFileObject(file.loid, my_file_object, this.$router);
    },

    filesUploaded() {
      this.refreshFirmFiles(this.fid, this.page, this.itemsPerPage)
    },
  },

}
</script>

<style scoped>
.files-header {
  align-items: flex-start;
  display: flex;
  min-width: 0;
}

.firm-files-toolbar {
  align-items: center;
  display: flex;
  flex: 1 1 auto;
  gap: 24px;
  justify-content: flex-end;
  margin-left: auto;
  min-width: 0;
}

.files-refresh-button {
  transform: translateY(1px);
}

.toolbar-info {
  flex: 0 0 auto;
  order: 1;
}

.files-info-dialog-copy p {
  margin-bottom: 12px;
}

.files-info-dialog-copy p:last-child {
  margin-bottom: 0;
}

.files-info-dialog-copy--compact {
  font-size: 0.94rem;
  line-height: 1.4;
}

.files-info-dialog-copy--compact p {
  margin-bottom: 8px;
}

.search-stack {
  display: flex;
  flex: 0 1 calc(100vw - 700px);
  flex-direction: column;
  margin-left: 8px;
  max-width: calc(100vw - 700px);
  min-width: 220px;
  order: 4;
  width: calc(100vw - 700px);
}

.search-input {
  min-width: 0;
  transform: translateY(2px);
  width: 100%;
}

.result-summary {
  font-variant-numeric: tabular-nums;
  min-width: 0;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  transform: translateY(2px);
  white-space: nowrap;
}

.supporting-text {
  color: rgba(0, 0, 0, 0.64);
}

.supporting-text strong {
  color: rgba(0, 0, 0, 0.78);
}

.table-options-copy {
  font-size: 0.93rem;
  line-height: 1.45;
}

.table-options-title {
  font-size: 1.2rem !important;
  line-height: 1.3;
}

.table-options-card {
  width: 100%;
}

.table-options-card--menu {
  max-width: calc(100vw - 32px);
  width: 420px;
}

.table-options-card--dialog {
  max-height: calc(100vh - 24px);
  max-width: calc(100vw - 16px);
  overflow-y: auto;
}

.table-options-field-label {
  color: rgba(0, 0, 0, 0.68);
  font-size: 0.77rem;
  font-weight: 600;
  line-height: 1.3;
  margin-bottom: 4px;
}

.table-options-note {
  line-height: 1.35;
}

.table-options-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.toolbar-action {
  flex: 0 0 auto;
  transform: translateY(2px);
  white-space: nowrap;
}

.toolbar-action--back {
  font-weight: 600;
}

.toolbar-action--primary {
  width: 208px;
  padding: 0 18px !important;
  order: 2;
  transform: translateY(0.5px);
}

.toolbar-action--options {
  order: 3;
}

.upload-button-wrap {
  display: flex;
  order: 5;
  transform: translate(-4px, -4px);
}

.upload-button-wrap ::v-deep .v-btn {
  margin-top: 0 !important;
}

@media (max-width: 960px) {
  .firm-files-toolbar {
    flex-wrap: wrap;
    gap: 4px 12px;
    justify-content: flex-start;
    margin-left: 0;
    margin-top: 0 !important;
  }

  .toolbar-action {
    min-width: 0 !important;
    padding: 0 2px !important;
    transform: none;
  }

  .toolbar-action--primary {
    transform: translateY(-1px);
    width: auto;
  }

  .toolbar-action .v-icon--left {
    margin-right: 2px;
  }

  .upload-button-wrap {
    order: 3;
    transform: none;
  }

  .upload-button-wrap ::v-deep .v-tooltip {
    margin-left: 0 !important;
    padding-right: 0 !important;
  }

  .upload-button-wrap ::v-deep .v-btn {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .search-stack {
    flex: 1 1 100%;
    margin: -8px 0;
    max-width: none;
    min-width: 180px;
    order: 4;
    width: 100%;
  }

  .table-options-card--dialog {
    max-height: calc(100vh - 16px);
  }

  .result-summary {
    flex: 1 1 100%;
    margin-top: 2px;
    transform: none;
  }
}
</style>
